import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, List, Package, Plus, X as XIcon, Loader2 } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import DOMPurify from 'dompurify';
import { useServiceAddonsStore } from '../../lib/stores/serviceAddonsStore';
import { listPriceForPeriod, monthlyBaseOf, parseBillingPrices } from '../../lib/utils/billingPrices';
import type { DealServiceFormData, ServiceAddon } from '../../lib/types';

interface DealServiceAddonsPickerProps {
  /** Id del servizio del catalogo (NON di deal_services). XOR con productId. */
  serviceId?: string;
  /** Id del prodotto magazzino. XOR con serviceId. */
  productId?: string;
  /**
   * Add-on attualmente selezionati nello stato del form (snapshot).
   * `service_addon_id` collega lo snapshot all'add-on master quando disponibile.
   */
  selectedAddons: NonNullable<DealServiceFormData['addons']>;
  /** Importo base della riga (prezzo servizio/prodotto scontato o lordo, prima degli add-on). */
  baseAmount: number;
  /** Notifica il parent con la nuova lista di add-on selezionati. */
  onChange: (addons: NonNullable<DealServiceFormData['addons']>) => void;
  /** Mostra l'header collapsibile (default true). */
  collapsible?: boolean;
  /**
   * Cornice del componente: header "Add-on", bordo e riepilogo del totale.
   * Va spenta quando il picker sta dentro un gruppo del pannello riga
   * servizio, che quelle informazioni le mostra già per conto suo.
   */
  chrome?: boolean;
  /**
   * Periodo di fatturazione della riga in mesi (1 = mensile, 12 = annuale).
   * Il prezzo a catalogo è quello del periodo base: qui viene riportato al
   * periodo della riga, applicando gli sconti per frequenza dell'add-on.
   */
  billingMonths?: number;
  /** Ricorrenza del servizio: dice se il prezzo a catalogo è mensile o annuale. */
  serviceType?: string | null;
  /** Label opzionale per il container. */
  className?: string;
}

/**
 * Sezione collassabile che mostra gli add-on disponibili per un servizio del
 * catalogo e permette di spuntare quelli opzionali. Gli obbligatori sono
 * sempre presenti e non deselezionabili.
 *
 * Il componente NON modifica direttamente editingService.amount: notifica il
 * parent con `onChange(addons)` e lascia che sia il parent a ricalcolare
 * l'amount = baseAmount + SUM(addons WHERE !is_included_in_base).
 *
 * Piano di riferimento: MD/plans/2026-05-30-service-addons-plan.md
 */
export function DealServiceAddonsPicker({
  serviceId,
  productId,
  selectedAddons,
  baseAmount,
  onChange,
  collapsible = true,
  chrome = true,
  billingMonths = 1,
  serviceType,
  className,
}: DealServiceAddonsPickerProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState<ServiceAddon[]>([]);

  // Stato per la creazione inline di un nuovo add-on (Notion-like). Quando
  // la riga si apre, `name` e `amount` sono editabili; Enter / blur su un
  // campo NON salva — l'utente conferma con il bottone ✓ o cancella con ✗.
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAmount, setNewAmount] = useState<string>('');
  const [newIsRequired, setNewIsRequired] = useState(false);
  const [creatingSubmit, setCreatingSubmit] = useState(false);
  const newNameInputRef = useRef<HTMLInputElement>(null);

  const fetchAddonsByService = useServiceAddonsStore((s) => s.fetchAddonsByService);
  const fetchAddonsByProduct = useServiceAddonsStore((s) => s.fetchAddonsByProduct);
  const getAddonsByServiceId = useServiceAddonsStore((s) => s.getAddonsByServiceId);
  const getAddonsByProductId = useServiceAddonsStore((s) => s.getAddonsByProductId);
  const createAddon = useServiceAddonsStore((s) => s.createAddon);

  // Modalità xor: target è service o product
  const targetKind: 'service' | 'product' | null = productId ? 'product' : serviceId ? 'service' : null;
  const isCustomService = !!serviceId && serviceId.startsWith('custom-');

  /**
   * Prezzo dell'add-on per il periodo della riga. A catalogo l'importo è quello
   * del periodo base (il mese, o l'anno sui servizi annuali): su una riga
   * annuale un add-on da 49 €/mese vale 588 €, meno l'eventuale sconto per
   * frequenza configurato sull'add-on stesso.
   */
  const catalogAmountFor = (addon: Pick<ServiceAddon, 'amount' | 'billing_prices'>): number => {
    const monthly = monthlyBaseOf(Number(addon.amount) || 0, serviceType);
    return listPriceForPeriod(monthly, parseBillingPrices(addon.billing_prices), billingMonths || 1);
  };

  /** Snapshot dell'add-on per questa offerta, col prezzo del periodo corrente. */
  const snapshotOf = (addon: ServiceAddon) => ({
    service_addon_id: addon.id,
    name: addon.name,
    description: addon.description ?? undefined,
    amount: catalogAmountFor(addon),
    is_required: addon.is_required,
    is_included_in_base: addon.is_included_in_base,
  });

  // ── Sotto-opzioni / fasce (parent_addon_id, 20260814) ──────────────────────
  // Le figlie non compaiono come righe autonome: vivono sotto il padre come
  // radio mutuamente esclusive. Il padre fa da selettore, il prezzo è della
  // fascia scelta, e lo snapshot porta il nome composto "Padre — Fascia" così
  // PDF e contratti restano leggibili senza toccare il renderer.
  const childrenOf = (parentId: string) =>
    available.filter((a) => a.parent_addon_id === parentId);

  const defaultChildOf = (parent: ServiceAddon): ServiceAddon | null => {
    const kids = childrenOf(parent.id);
    if (kids.length === 0) return null;
    return kids.find((k) => k.is_default_option) ?? kids[0];
  };

  /** Snapshot di una fascia: nome composto e flag comportamentali dal padre.
   *  La descrizione del padre NON si eredita: spesso è una nota interna di
   *  catalogo e finirebbe nel PDF del cliente. */
  const snapshotOfChild = (parent: ServiceAddon, child: ServiceAddon) => ({
    service_addon_id: child.id,
    name: `${parent.name} — ${child.name}`,
    description: child.description ?? undefined,
    amount: catalogAmountFor(child),
    is_required: parent.is_required,
    is_included_in_base: child.is_included_in_base || parent.is_included_in_base,
  });

  /** Snapshot "giusto" per un add-on di primo livello: fascia default se ne ha. */
  const snapshotFor = (addon: ServiceAddon) => {
    const kid = defaultChildOf(addon);
    return kid ? snapshotOfChild(addon, kid) : snapshotOf(addon);
  };

  /** Tutti gli id che rappresentano l'add-on nella selezione (sé + figlie). */
  const idsOf = (addon: ServiceAddon): Set<string> => {
    const ids = new Set<string>([addon.id]);
    for (const k of childrenOf(addon.id)) ids.add(k.id);
    return ids;
  };

  // Carica gli add-on disponibili per il target (service o product).
  useEffect(() => {
    let cancelled = false;

    if (!targetKind) {
      setAvailable([]);
      return;
    }
    // I servizi personalizzati (service_id 'custom-*') non hanno catalogo add-on.
    if (targetKind === 'service' && isCustomService) {
      setAvailable([]);
      return;
    }

    // Cache-first
    const cached = targetKind === 'service'
      ? getAddonsByServiceId(serviceId!)
      : getAddonsByProductId(productId!);
    if (cached.length > 0) setAvailable(cached);

    setLoading(true);
    const fetcher = targetKind === 'service'
      ? fetchAddonsByService(serviceId!)
      : fetchAddonsByProduct(productId!);

    fetcher
      .then((fetched) => {
        if (cancelled) return;
        setAvailable(fetched);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, productId]);

  // Un add-on di primo livello "è selezionato" anche quando in selezione c'è
  // una sua fascia (le figlie rappresentano il padre).
  const representsSelected = (addon: ServiceAddon) => {
    const ids = idsOf(addon);
    return selectedAddons.some((sel) => sel.service_addon_id && ids.has(sel.service_addon_id));
  };

  // Pre-seleziona gli obbligatori e quelli inclusi nel base se l'utente
  // non li ha già nello stato (es. apertura del drawer la prima volta).
  useEffect(() => {
    if (available.length === 0) return;

    const topLevelAll = available.filter((a) => !a.parent_addon_id);
    const mustBePresent = topLevelAll.filter(
      (a) => a.is_required || a.is_included_in_base
    );
    const missing = mustBePresent.filter((a) => !representsSelected(a));

    // Default dei gruppi a scelta obbligatoria: se nessun membro del gruppo è
    // selezionato, pre-seleziona il primo. SOLO quando la riga parte da base 0
    // (pattern "base 0 + gruppo piani"): su righe storiche con base già
    // valorizzato aggiungere un default cambierebbe il totale in silenzio.
    const groupDefaults: ServiceAddon[] = [];
    if (baseAmount === 0) {
      const groups = new Map<string, ServiceAddon[]>();
      for (const a of topLevelAll) {
        if (a.is_required || !a.addon_group) continue;
        const list = groups.get(a.addon_group) ?? [];
        list.push(a);
        groups.set(a.addon_group, list);
      }
      for (const members of groups.values()) {
        const anySelected = members.some((a) => representsSelected(a));
        if (!anySelected && members.length > 0) groupDefaults.push(members[0]);
      }
    }

    if (missing.length === 0 && groupDefaults.length === 0) return;

    const next = [
      ...selectedAddons,
      ...[...missing, ...groupDefaults].map(snapshotFor),
    ];
    onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  // Le figlie (fasce) non compaiono come righe autonome nelle liste.
  const requiredAddons = useMemo(
    () => available.filter((a) => a.is_required && !a.parent_addon_id),
    [available]
  );
  const optionalAddons = useMemo(
    () => available.filter((a) => !a.is_required && !a.parent_addon_id),
    [available]
  );

  // Opzionali divisi tra gruppi a scelta obbligatoria e liberi (mantiene
  // l'ordinamento per sort_order già applicato dallo store).
  const { groupedOptional, ungroupedOptional } = useMemo(() => {
    const groups = new Map<string, ServiceAddon[]>();
    const rest: ServiceAddon[] = [];
    for (const a of optionalAddons) {
      if (a.addon_group) {
        const list = groups.get(a.addon_group) ?? [];
        list.push(a);
        groups.set(a.addon_group, list);
      } else {
        rest.push(a);
      }
    }
    return { groupedOptional: [...groups.entries()], ungroupedOptional: rest };
  }, [optionalAddons]);

  // Child-aware: un padre risulta selezionato anche tramite una sua fascia.
  const isAddonSelected = (addonId: string) => {
    if (selectedAddons.some((sel) => sel.service_addon_id === addonId)) return true;
    return childrenOf(addonId).some((k) =>
      selectedAddons.some((sel) => sel.service_addon_id === k.id)
    );
  };

  // Snapshot selezionati che non corrispondono a nessun add-on del catalogo
  // corrente (master eliminato/archiviato o snapshot senza service_addon_id).
  const snapshotOnlySelected = selectedAddons.filter(
    (sel) =>
      !sel.is_required &&
      (!sel.service_addon_id || !available.some((a) => a.id === sel.service_addon_id))
  );

  // Per un padre con fasce restituisce lo snapshot della fascia scelta.
  const getSelected = (addonId: string) =>
    selectedAddons.find((sel) => sel.service_addon_id === addonId) ??
    selectedAddons.find((sel) =>
      childrenOf(addonId).some((k) => k.id === sel.service_addon_id)
    );

  // Modifica la descrizione SNAPSHOT dell'add-on per questa offerta.
  // Non tocca il master service_addons: la personalizzazione vive solo
  // in deal_service_addons (vedi persistDealServiceAddons in dealStore).
  const updateSelectedDescription = (addonId: string, description: string) => {
    onChange(
      selectedAddons.map((sel) =>
        sel.service_addon_id === addonId
          ? { ...sel, description: description === '' ? undefined : description }
          : sel
      )
    );
  };

  // Apre la riga di creazione inline e focusa subito il campo nome.
  // Mantiene la sezione espansa anche se era collapsed.
  const openCreateRow = () => {
    setCreating(true);
    setCollapsed(false);
    setNewName('');
    setNewAmount('');
    setNewIsRequired(false);
    // Focus dopo che il render ha montato l'input.
    setTimeout(() => newNameInputRef.current?.focus(), 0);
  };

  const cancelCreate = () => {
    setCreating(false);
    setNewName('');
    setNewAmount('');
    setNewIsRequired(false);
  };

  const submitCreate = async () => {
    const trimmedName = newName.trim();
    if (!trimmedName || creatingSubmit) return;
    const amountNum = Number(newAmount);
    const typed = Number.isFinite(amountNum) && amountNum >= 0 ? amountNum : 0;
    // L'importo digitato è quello del periodo che l'utente sta vedendo (su una
    // riga annuale scrive il prezzo dell'anno), mentre il catalogo tiene il
    // prezzo del periodo base: si riporta indietro, così lo snapshot ricalcolato
    // restituisce esattamente il numero digitato.
    const periodFactor = monthlyBaseOf(1, serviceType) * (billingMonths || 1);
    const safeAmount = periodFactor > 0 ? Math.round((typed / periodFactor) * 100) / 100 : typed;

    try {
      setCreatingSubmit(true);
      const created = await createAddon({
        service_id: targetKind === 'service' ? serviceId! : null,
        product_id: targetKind === 'product' ? productId! : null,
        name: trimmedName,
        amount: safeAmount,
        is_required: newIsRequired,
        is_included_in_base: false,
      });
      // Aggiungi al catalogo locale del picker (lo store già aggiorna global).
      setAvailable((prev) => [...prev, created]);
      // Pre-seleziona il nuovo add-on per l'offerta corrente: se obbligatorio
      // verrebbe comunque auto-aggiunto dall'effect; per gli opzionali invece
      // l'utente ha appena scelto di crearlo → ha senso averlo già spuntato.
      onChange([...selectedAddons, snapshotOf(created)]);
      cancelCreate();
    } catch (err) {
      console.error('Errore creazione add-on inline:', err);
    } finally {
      setCreatingSubmit(false);
    }
  };

  const handleNewNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitCreate();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelCreate();
    }
  };

  // ── Gruppi a scelta obbligatoria (addon_group, 20260812) ──────────────────
  // Tra gli add-on opzionali che condividono lo stesso addon_group va
  // selezionato ALMENO UNO; con addon_group_exclusive la scelta è radio.
  const optionalOfGroup = (group: string) =>
    available.filter((a) => !a.is_required && !a.parent_addon_id && a.addon_group === group);

  const selectedCountInGroup = (group: string) =>
    optionalOfGroup(group).filter((a) => isAddonSelected(a.id)).length;

  const toggleOptional = (addon: ServiceAddon) => {
    const exists = isAddonSelected(addon.id);
    const group = addon.addon_group ?? null;
    const ownIds = idsOf(addon);

    if (exists) {
      // Vincolo "almeno uno": l'ultimo selezionato del gruppo non si toglie.
      if (group && selectedCountInGroup(group) <= 1) return;
      onChange(
        selectedAddons.filter(
          (sel) => !sel.service_addon_id || !ownIds.has(sel.service_addon_id)
        )
      );
      return;
    }

    // Padre con fasce: si entra con la fascia di default.
    const snapshot = snapshotFor(addon);

    if (group && addon.addon_group_exclusive) {
      // Gruppo esclusivo (radio): selezionare questo deseleziona gli altri
      // membri del gruppo, comprese le loro eventuali fasce.
      const groupIds = new Set<string>();
      for (const m of optionalOfGroup(group)) {
        for (const id of idsOf(m)) groupIds.add(id);
      }
      onChange([
        ...selectedAddons.filter(
          (sel) => !sel.service_addon_id || !groupIds.has(sel.service_addon_id)
        ),
        snapshot,
      ]);
      return;
    }

    onChange([...selectedAddons, snapshot]);
  };

  // Cambia la fascia di un padre selezionato: sostituisce lo snapshot della
  // sorella con quello della fascia scelta (prezzo e nome composti a nuovo).
  const pickFascia = (parent: ServiceAddon, child: ServiceAddon) => {
    const kidIds = new Set(childrenOf(parent.id).map((k) => k.id));
    const already = selectedAddons.find(
      (sel) => sel.service_addon_id && kidIds.has(sel.service_addon_id)
    );
    if (already?.service_addon_id === child.id) return;
    onChange([
      ...selectedAddons.filter(
        (sel) => !sel.service_addon_id || !kidIds.has(sel.service_addon_id)
      ),
      snapshotOfChild(parent, child),
    ]);
  };

  // Somma degli add-on che ALTERANO il prezzo (escludendo gli "included_in_base").
  const addonsBillableTotal = selectedAddons
    .filter((a) => !a.is_included_in_base)
    .reduce((sum, a) => sum + (Number.isFinite(a.amount) ? a.amount : 0), 0);

  const rowTotal = baseAmount + addonsBillableTotal;

  // Riga di un add-on opzionale. control='radio' per i gruppi esclusivi.
  // Volutamente un <div>, NON un <label>: il label inoltra il click nativo
  // all'input e insieme all'onClick del wrapper produceva un doppio toggle
  // (netto: nessuna spunta).
  const renderOptionalRow = (addon: ServiceAddon, control: 'checkbox' | 'radio') => {
    const checked = isAddonSelected(addon.id);
    const sel = getSelected(addon.id);
    const kids = childrenOf(addon.id);
    const hasKids = kids.length > 0;
    // Prezzo mostrato da spento: quello della fascia default, se il padre ha fasce.
    const unselectedPrice = catalogAmountFor(
      hasKids ? (defaultChildOf(addon) ?? addon) : addon
    );
    return (
      <div
        key={addon.id}
        className={`text-sm py-1.5 px-2 rounded-md cursor-pointer transition-colors ${
          checked
            ? 'bg-blue-50 dark:bg-blue-900/20'
            : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
        }`}
        onClick={(e) => {
          // Tutta la riga è hit target, tranne i controlli interni
          // (checkbox/radio, editor descrizione).
          if (!(e.target as HTMLElement).closest('input, textarea, button')) {
            toggleOptional(addon);
          }
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <input
              type={control}
              name={control === 'radio' ? `addon-group-${addon.addon_group}` : undefined}
              className={`w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0 ${
                control === 'checkbox' ? 'rounded' : ''
              }`}
              checked={checked}
              onChange={() => toggleOptional(addon)}
            />
            <span className="text-black dark:text-white truncate">{addon.name}</span>
            {hasKids && !checked && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/[0.05] dark:bg-white/[0.08] text-black/50 dark:text-white/50 shrink-0">
                {kids.length} fasce
              </span>
            )}
          </div>
          <span className="text-xs text-black/70 dark:text-white/70 shrink-0">
            {/* Prezzo dallo SNAPSHOT quando selezionato: l'offerta deve mostrare
                il valore venduto, non il listino attuale del catalogo. */}
            € {(checked && sel ? sel.amount : unselectedPrice).toFixed(2)}
          </span>
        </div>
        {checked && hasKids && (
          <div className="pl-6 mt-1 space-y-0.5">
            {kids.map((k) => {
              const kChecked = sel?.service_addon_id === k.id;
              return (
                <div
                  key={k.id}
                  className="flex items-center justify-between text-[13px] py-0.5 px-1 -mx-1 rounded cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!(e.target as HTMLElement).closest('input')) pickFascia(addon, k);
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <input
                      type="radio"
                      name={`addon-fascia-${addon.id}`}
                      className="w-3.5 h-3.5 border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                      checked={kChecked}
                      onChange={() => pickFascia(addon, k)}
                    />
                    <span className="text-black/80 dark:text-white/80 truncate">{k.name}</span>
                    {k.is_default_option && (
                      <span className="text-[10px] text-black/35 dark:text-white/35 shrink-0">default</span>
                    )}
                  </div>
                  <span className="text-xs text-black/60 dark:text-white/60 shrink-0">
                    € {catalogAmountFor(k).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {checked && sel ? (
          <div className="pl-6">
            <AddonDescriptionEditor
              value={sel.description ?? ''}
              onChange={(v) =>
                updateSelectedDescription(sel.service_addon_id ?? addon.id, v)
              }
            />
          </div>
        ) : (
          addon.description && (
            <p className="pl-6 mt-0.5 text-xs text-black/40 dark:text-white/40 leading-snug">
              {addon.description}
            </p>
          )
        )}
      </div>
    );
  };

  // Nessun add-on disponibile e nessun selezionato — mostra un placeholder
  // Notion-like che permette di crearne uno al volo. Non disponibile per i
  // servizi custom (service_id `custom-*`) né senza target valorizzato.
  if (available.length === 0 && selectedAddons.length === 0 && !creating) {
    if (!targetKind || isCustomService) return null;
    return (
      <div className={className ?? ''}>
        <button
          type="button"
          onClick={openCreateRow}
          className="inline-flex items-center gap-1.5 text-[13px] text-black/55 dark:text-white/55 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] px-2 py-1 rounded-md transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Aggiungi add-on</span>
        </button>
      </div>
    );
  }

  // Caso creazione inline con stato vuoto: mostriamo solo la riga di edit,
  // senza il wrapper card pieno.
  if (available.length === 0 && selectedAddons.length === 0 && creating) {
    return (
      <div className={className ?? ''}>
        <CreateAddonRow
          name={newName}
          amount={newAmount}
          isRequired={newIsRequired}
          submitting={creatingSubmit}
          inputRef={newNameInputRef}
          onNameChange={setNewName}
          onAmountChange={setNewAmount}
          onIsRequiredChange={setNewIsRequired}
          onSubmit={submitCreate}
          onCancel={cancelCreate}
          onNameKeyDown={handleNewNameKeyDown}
        />
      </div>
    );
  }

  return (
    <div
      className={
        chrome
          ? `rounded-lg border border-black/10 dark:border-white/10 bg-white/50 dark:bg-white/5 ${className ?? ''}`
          : (className ?? '')
      }
    >
      {/* Header */}
      {chrome && (
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => collapsible && setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
      >
        <div className="flex items-center gap-2">
          <Package size={16} className="text-black/60 dark:text-white/60" />
          <span className="text-sm font-medium text-black dark:text-white">Add-on</span>
          {available.length > 0 && (
            <span className="text-xs text-black/50 dark:text-white/50">
              ({selectedAddons.filter((a) => !a.is_included_in_base).length}/
              {optionalAddons.length} opzionali
              {requiredAddons.length > 0 ? ` · ${requiredAddons.length} obbligatori` : ''})
            </span>
          )}
        </div>
        {collapsible && (
          collapsed
            ? <ChevronDown size={16} className="text-black/60 dark:text-white/60" />
            : <ChevronUp size={16} className="text-black/60 dark:text-white/60" />
        )}
      </button>
      )}

      {!collapsed && (
        <div className={chrome ? 'px-4 pb-4 space-y-4 border-t border-black/5 dark:border-white/10' : 'space-y-3'}>
          {loading && available.length === 0 && (
            <div className="text-xs text-black/50 dark:text-white/50 pt-3">
              Caricamento add-on...
            </div>
          )}

          {/* Obbligatori */}
          {requiredAddons.length > 0 && (
            <div className="pt-3">
              <h4 className="text-xs uppercase tracking-wide text-black/50 dark:text-white/50 mb-2">
                Obbligatori inclusi
              </h4>
              <div className="space-y-1.5">
                {requiredAddons.map((addon) => {
                  const sel = getSelected(addon.id);
                  const kids = childrenOf(addon.id);
                  return (
                    <div
                      key={addon.id}
                      className="text-sm py-1.5 px-2 rounded-md bg-black/[0.03] dark:bg-white/[0.04]"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"
                            aria-hidden
                          />
                          <span className="text-black dark:text-white truncate">{addon.name}</span>
                          <span className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-medium">
                            obbligatorio
                          </span>
                        </div>
                        <span className="text-xs text-black/60 dark:text-white/60 shrink-0">
                          {addon.is_included_in_base
                            ? 'compreso'
                            : `€ ${(sel?.amount ?? catalogAmountFor(defaultChildOf(addon) ?? addon)).toFixed(2)}`}
                        </span>
                      </div>
                      {kids.length > 0 && (
                        <div className="pl-4 mt-1 space-y-0.5">
                          {kids.map((k) => (
                            <div
                              key={k.id}
                              className="flex items-center justify-between text-[13px] py-0.5 px-1 -mx-1 rounded cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
                              onClick={(e) => {
                                if (!(e.target as HTMLElement).closest('input')) pickFascia(addon, k);
                              }}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <input
                                  type="radio"
                                  name={`addon-fascia-${addon.id}`}
                                  className="w-3.5 h-3.5 border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                                  checked={sel?.service_addon_id === k.id}
                                  onChange={() => pickFascia(addon, k)}
                                />
                                <span className="text-black/80 dark:text-white/80 truncate">{k.name}</span>
                                {k.is_default_option && (
                                  <span className="text-[10px] text-black/35 dark:text-white/35 shrink-0">default</span>
                                )}
                              </div>
                              <span className="text-xs text-black/60 dark:text-white/60 shrink-0">
                                € {catalogAmountFor(k).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {sel && (
                        <div className="pl-4">
                          <AddonDescriptionEditor
                            value={sel.description ?? ''}
                            onChange={(v) =>
                              updateSelectedDescription(sel.service_addon_id ?? addon.id, v)
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Opzionali */}
          {(optionalAddons.length > 0 || creating || !isCustomService) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs uppercase tracking-wide text-black/50 dark:text-white/50">
                  {optionalAddons.length > 0 ? 'Opzionali' : 'Crea il primo add-on'}
                </h4>
                {!creating && !isCustomService && (
                  <button
                    type="button"
                    onClick={openCreateRow}
                    className="inline-flex items-center gap-1 text-[12px] text-black/55 dark:text-white/55 hover:text-black dark:hover:text-white px-1.5 py-0.5 rounded transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
                  >
                    <Plus className="h-3 w-3" />
                    <span>Nuovo</span>
                  </button>
                )}
              </div>
              {creating && (
                <div className="mb-2">
                  <CreateAddonRow
                    name={newName}
                    amount={newAmount}
                    isRequired={newIsRequired}
                    submitting={creatingSubmit}
                    inputRef={newNameInputRef}
                    onNameChange={setNewName}
                    onAmountChange={setNewAmount}
                    onIsRequiredChange={setNewIsRequired}
                    onSubmit={submitCreate}
                    onCancel={cancelCreate}
                    onNameKeyDown={handleNewNameKeyDown}
                  />
                </div>
              )}
              {groupedOptional.map(([groupName, members]) => {
                const exclusive = members.some((m) => m.addon_group_exclusive);
                const selCount = members.filter((m) => isAddonSelected(m.id)).length;
                return (
                  <div key={groupName} className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="text-[11px] uppercase tracking-wide text-black/45 dark:text-white/45">
                        {groupName}
                      </h5>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/[0.05] dark:bg-white/[0.08] text-black/55 dark:text-white/55">
                        {exclusive ? 'scegline uno' : 'almeno uno'}
                      </span>
                      {selCount === 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/25 text-red-600 dark:text-red-400 font-medium">
                          selezione obbligatoria
                        </span>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {members.map((addon) =>
                        renderOptionalRow(addon, exclusive ? 'radio' : 'checkbox')
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="space-y-1.5">
                {ungroupedOptional.map((addon) => renderOptionalRow(addon, 'checkbox'))}
              </div>

              {/* Add-on presenti nello snapshot dell'offerta ma non (più) nel
                  catalogo del servizio: senza questo blocco sarebbero invisibili
                  in edit pur contando nel totale. Restano deselezionabili. */}
              {snapshotOnlySelected.length > 0 && (
                <div className="mt-2">
                  <h5 className="text-[11px] uppercase tracking-wide text-black/45 dark:text-white/45 mb-1">
                    Selezionati (non più a catalogo)
                  </h5>
                  <div className="space-y-1.5">
                    {snapshotOnlySelected.map((sel, i) => (
                      <div
                        key={sel.service_addon_id ?? `snapshot-${i}`}
                        className="text-sm py-1.5 px-2 rounded-md bg-blue-50 dark:bg-blue-900/20"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                              checked
                              onChange={() =>
                                onChange(selectedAddons.filter((x) => x !== sel))
                              }
                            />
                            <span className="text-black dark:text-white truncate">{sel.name}</span>
                          </div>
                          <span className="text-xs text-black/70 dark:text-white/70 shrink-0">
                            € {sel.amount.toFixed(2)}
                          </span>
                        </div>
                        <div className="pl-6">
                          <AddonDescriptionEditor
                            value={sel.description ?? ''}
                            onChange={(v) =>
                              onChange(
                                selectedAddons.map((x) =>
                                  x === sel ? { ...x, description: v === '' ? undefined : v } : x
                                )
                              )
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Totale riga — con chrome spento lo mostra il footer del pannello */}
          {chrome && (
          <div className="pt-3 border-t border-black/5 dark:border-white/10 space-y-1 text-sm">
            <div className="flex justify-between text-black/70 dark:text-white/70">
              <span>Prezzo base servizio</span>
              <span>€ {baseAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-black/70 dark:text-white/70">
              <span>Add-on selezionati</span>
              <span>€ {addonsBillableTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold text-black dark:text-white pt-1 border-t border-dashed border-black/10 dark:border-white/15">
              <span>Totale riga</span>
              <span>€ {rowTotal.toFixed(2)}</span>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Un testo salvato prima del rich text non ha tag: va avvolto per rileggerlo. */
function toHtml(value: string): string {
  if (!value) return '';
  return /<[a-z][\s\S]*>/i.test(value) ? value : `<p>${value}</p>`;
}

/**
 * Descrizione SNAPSHOT di un add-on selezionato: si legge come testo e diventa
 * un editor solo quando la clicchi, come le proprietà del drawer. Grassetto,
 * corsivo ed elenco puntato — la stessa formattazione della descrizione del
 * servizio, in scala ridotta.
 *
 * Modifica solo la copia per l'offerta corrente (deal_service_addons), mai il
 * master del catalogo.
 */
function AddonDescriptionEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const html = toHtml(value);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        className="group/desc w-full flex items-baseline gap-2 mt-0.5 px-1 py-0.5 -mx-1 rounded text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.04] transition-colors"
      >
        {html ? (
          <span
            className="min-w-0 flex-1 text-xs text-black/55 dark:text-white/55 leading-snug [&_p]:m-0 [&_ul]:my-0 [&_ul]:pl-4 [&_ul]:list-disc"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
          />
        ) : (
          <span className="flex-1 text-xs italic text-black/30 dark:text-white/30">Nessuna descrizione</span>
        )}
        <span className="flex-shrink-0 text-[11px] text-black/45 dark:text-white/45 opacity-0 group-hover/desc:opacity-100 transition-opacity">
          modifica
        </span>
      </button>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()} className="mt-1">
      <AddonRichEditor
        html={html}
        onChange={onChange}
        onDone={() => setEditing(false)}
      />
    </div>
  );
}

/** Editor TipTap minimale, montato solo mentre si scrive. */
function AddonRichEditor({
  html,
  onChange,
  onDone,
}: {
  html: string;
  onChange: (v: string) => void;
  onDone: () => void;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
    ],
    content: html,
    autofocus: 'end',
    onUpdate: ({ editor: ed }) => {
      const next = ed.getHTML();
      onChange(next === '<p></p>' ? '' : next);
    },
  });

  const btn = (active: boolean) =>
    `px-1.5 py-0.5 rounded text-[12px] leading-none transition-colors ${
      active
        ? 'bg-[#2383E2]/10 text-[#2383E2]'
        : 'text-black/50 dark:text-white/50 hover:bg-black/[0.05] dark:hover:bg-white/[0.08]'
    }`;

  return (
    <div className="rounded bg-black/[0.03] dark:bg-white/[0.05] px-1.5 py-1">
      <div className="flex items-center gap-0.5 pb-1">
        <button type="button" title="Grassetto" className={btn(!!editor?.isActive('bold'))}
          onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleBold().run(); }}>
          <strong>B</strong>
        </button>
        <button type="button" title="Corsivo" className={btn(!!editor?.isActive('italic'))}
          onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleItalic().run(); }}>
          <em>I</em>
        </button>
        <button type="button" title="Elenco puntato" className={btn(!!editor?.isActive('bulletList'))}
          onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleBulletList().run(); }}>
          <List className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={onDone}
          className="ml-auto text-[11px] font-medium px-2 py-0.5 rounded bg-[#37352F] text-white hover:opacity-90 transition-opacity"
        >
          Fatto
        </button>
      </div>
      <EditorContent
        editor={editor}
        className="text-xs text-black/70 dark:text-white/70 [&_.ProseMirror]:!border-0 [&_.ProseMirror]:!bg-transparent [&_.ProseMirror]:!p-0 [&_.ProseMirror]:!shadow-none [&_.ProseMirror]:!min-h-[28px] [&_.ProseMirror]:focus:outline-none [&_p]:m-0 [&_ul]:my-0 [&_ul]:pl-4 [&_ul]:list-disc"
      />
    </div>
  );
}

/**
 * Riga di creazione inline Notion-like per un add-on. Tutti i campi vivono in
 * una singola riga (nome + prezzo + opzionale 'obbligatorio' + azioni ✓/✗).
 * Salvataggio con Enter sul nome o click sul check; annullamento con Esc o ✗.
 */
interface CreateAddonRowProps {
  name: string;
  amount: string;
  isRequired: boolean;
  submitting: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onNameChange: (v: string) => void;
  onAmountChange: (v: string) => void;
  onIsRequiredChange: (v: boolean) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onNameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

function CreateAddonRow({
  name,
  amount,
  isRequired,
  submitting,
  inputRef,
  onNameChange,
  onAmountChange,
  onIsRequiredChange,
  onSubmit,
  onCancel,
  onNameKeyDown,
}: CreateAddonRowProps) {
  const canSubmit = name.trim().length > 0 && !submitting;
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-black/10 dark:border-white/15 bg-white dark:bg-white/[0.03]">
      <input
        ref={inputRef}
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        onKeyDown={onNameKeyDown}
        placeholder="Nome add-on"
        disabled={submitting}
        className="flex-1 min-w-0 bg-transparent text-[13px] text-black dark:text-white placeholder-black/35 dark:placeholder-white/35 outline-none"
      />
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-[11px] text-black/45 dark:text-white/45">€</span>
        <input
          type="number"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
          placeholder="0"
          min="0"
          step="any"
          disabled={submitting}
          className="w-20 bg-transparent text-[13px] text-black dark:text-white text-right outline-none border-b border-transparent focus:border-black/20 dark:focus:border-white/20"
        />
      </div>
      <label className="flex items-center gap-1 shrink-0 text-[11px] text-black/55 dark:text-white/55 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isRequired}
          onChange={(e) => onIsRequiredChange(e.target.checked)}
          disabled={submitting}
          className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span>Obbligatorio</span>
      </label>
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSubmit}
        title="Crea add-on (Enter)"
        className="p-1 rounded text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        title="Annulla (Esc)"
        className="p-1 rounded text-black/40 dark:text-white/40 hover:text-black hover:bg-black/[0.04] dark:hover:text-white dark:hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
